import { useCallback, useState } from "react";

import {
  markDiffPanelOpenedForThread,
  resolveHasOpenedDiffForThread,
} from "./-chatThreadRoute.logic";

/**
 * Whether this thread's diff panel has ever been opened.
 *
 * The panel is expensive to mount, so it is kept out of the tree until first
 * use — but "first use" is per thread, not per session. Switching threads has
 * to forget, or the second thread would mount a diff panel the user never asked
 * for; that is what the tracked `threadKey` is for.
 *
 * The decisions live in the route's logic module and are already tested there.
 * This is the state they need, kept in one place instead of three.
 */
export function useDiffPanelMountState(input: {
  readonly currentThreadKey: string | null;
  readonly diffOpen: boolean;
}): { readonly hasOpenedDiff: boolean; readonly markDiffOpened: () => void } {
  const { currentThreadKey, diffOpen } = input;
  const [state, setState] = useState(() => ({
    threadKey: currentThreadKey,
    hasOpenedDiff: diffOpen,
  }));

  const markDiffOpened = useCallback(() => {
    setState((previous) => markDiffPanelOpenedForThread({ currentThreadKey, previous }));
  }, [currentThreadKey]);

  return {
    hasOpenedDiff: resolveHasOpenedDiffForThread({ currentThreadKey, diffOpen, state }),
    markDiffOpened,
  };
}
